(() => {
    if (customElements.get('omni-quickview')) return;

    const PDP_COLOR_BRIDGE_KEY = '__omniPdpColorBridgeV1';
    const UNIVERSAL_OPEN_EVENT = 'omni:quick-view:open';
    const UNIVERSAL_OPENED_EVENT = 'omni:quick-view:opened';
    const UNIVERSAL_ERROR_EVENT = 'omni:quick-view:error';
    const QUICK_VIEW_OPEN_CLASS = 'omni-quickview-open';
    const MODAL_HISTORY_STATE_KEY = '__omniQuickview';
    const PDP_CUSTOMIZER_LAUNCH_PARAM = 'omni_qv_customize';
    const CUSTOMIZER_ORIGIN = 'https://configurator.inforcloudsuite.com';
    let themeEventsModulePromise = null;

    function loadThemeEvents() {
        if (!themeEventsModulePromise) {
            themeEventsModulePromise = import('@theme/events');
        }

        return themeEventsModulePromise;
    }

    function installPdpColorBridge() {
        if (window[PDP_COLOR_BRIDGE_KEY]) return;
        window[PDP_COLOR_BRIDGE_KEY] = true;

        const colorInputSelector = 'input[type="radio"][name^="color-selector-"]';
        let syncTimer = null;

        const isPdpColorInput = input => {
            return !!(
                input?.matches?.(colorInputSelector) &&
                !input.closest('omni-quickview, [data-modal]')
            );
        };

        const publishColor = input => {
            if (!isPdpColorInput(input) || !input.checked) return;

            const color = input.dataset.colorName || input.value || '';
            if (!color) return;

            document.cookie = `pdp_selected_color=${encodeURIComponent(color)}; path=/; max-age=86400; SameSite=Lax`;
            document.dispatchEvent(new CustomEvent('pdp:color-change', {
                detail: { color, source: 'pdp' }
            }));
        };

        const publishCheckedColor = () => {
            const checkedInput = Array.from(document.querySelectorAll(`${colorInputSelector}:checked`))
                .find(isPdpColorInput);
            publishColor(checkedInput);
        };

        document.addEventListener('change', event => publishColor(event.target));
        document.addEventListener('click', event => {
            const trigger = event.target.closest?.('label[for^="color-"], .color-option');
            if (!trigger || trigger.closest('omni-quickview, [data-modal]')) return;

            window.clearTimeout(syncTimer);
            syncTimer = window.setTimeout(publishCheckedColor, 0);
        });

    }

    installPdpColorBridge();


    function launchPdpCustomizerFromQuickview() {
        const pageUrl = new URL(window.location.href);
        if (pageUrl.searchParams.get(PDP_CUSTOMIZER_LAUNCH_PARAM) !== '1') return;

        // Make the launch one-shot. Returning from Infor must restore the PDP
        // without immediately sending the customer back into the configurator.
        pageUrl.searchParams.delete(PDP_CUSTOMIZER_LAUNCH_PARAM);
        window.history.replaceState(window.history.state, '', pageUrl.toString());

        const launch = () => {
            const customizeButton = Array.from(document.querySelectorAll(
                'button.btn--cpq-outline[data-item-number][data-redirect-url]'
            )).find(button => !button.disabled);

            if (!customizeButton) return false;

            customizeButton.click();
            return true;
        };

        const start = () => {
            // The no-sizes order form used by custom poms and bows attaches its
            // CPQ handler from DOMContentLoaded. Run after every listener for that
            // event has finished so the first programmatic click is not discarded.
            window.setTimeout(() => {
                if (launch()) return;

                const launchObserver = new MutationObserver(() => {
                    if (launch()) launchObserver.disconnect();
                });

                launchObserver.observe(document.documentElement, {
                    childList: true,
                    subtree: true,
                    attributes: true,
                    attributeFilter: ['disabled']
                });

                window.setTimeout(() => launchObserver.disconnect(), 5000);
            }, 0);
        };

        // Deferred scripts execute while readyState is "interactive", before
        // DOMContentLoaded. The no-sizes CPQ setup is not ready until that event.
        if (document.readyState === 'complete') {
            start();
        } else {
            document.addEventListener('DOMContentLoaded', start, { once: true });
        }
    }

    launchPdpCustomizerFromQuickview();

    let cartPageRefreshPending = false;
    document.addEventListener('cart:item-added', () => {
        if (!/\/cart\/?$/.test(window.location.pathname) || cartPageRefreshPending) return;
        cartPageRefreshPending = true;
        window.setTimeout(() => window.location.reload(), 0);
    });

    const modalRuntime = (() => {
        const hosts = new Set();
        const openedHosts = [];
        let lastInteractionElement = null;

        const getActiveHosts = () => {
            const orderedHosts = openedHosts.filter(
                host => hosts.has(host) && host.isConnected && host.modal?.classList.contains('active')
            );
            const unorderedHosts = Array.from(hosts).filter(
                host => host.isConnected && host.modal?.classList.contains('active') && !orderedHosts.includes(host)
            );

            return [...orderedHosts, ...unorderedHosts];
        };

        const getActiveHost = () => {
            return getActiveHosts().at(-1);
        };

        const syncDocumentOpenState = () => {
            document.documentElement.classList.toggle(
                QUICK_VIEW_OPEN_CLASS,
                getActiveHosts().length > 0
            );
        };

        const getGlobalHost = () => {
            return Array.from(hosts).find(
                host => host.isConnected && host.hasAttribute('data-omni-quickview-global')
            ) || document.querySelector('omni-quickview[data-omni-quickview-global]');
        };

        const dispatchUniversalError = (error, detail = {}) => {
            const message = error instanceof Error ? error.message : String(error);
            console.error('[OmniQuickview]', error);
            window.dispatchEvent(new CustomEvent(UNIVERSAL_ERROR_EVENT, {
                detail: {
                    productUrl: detail?.productUrl || detail?.url || '',
                    variantId: detail?.variantId || '',
                    message
                }
            }));
        };

        const removeOpenedHost = host => {
            const index = openedHosts.indexOf(host);
            if (index !== -1) openedHosts.splice(index, 1);
        };

        const closeAllActiveHosts = () => {
            const activeHosts = getActiveHosts().reverse();

            activeHosts.forEach((host, index) => {
                host.closeModal({ restoreFocus: index === activeHosts.length - 1 });
            });
        };

        const handleReviewNavigation = event => {
            const reviewWrapper = event.target.closest?.('[data-pr-product-url]');
            if (!reviewWrapper) return;

            const reviewUi = event.target.closest?.(
                '.pr-snippet-wrapper, [data-powerreviews-card-snippet], [data-powerreviews-snippet], .p-w-r'
            );
            const productUrl = reviewWrapper.dataset.prProductUrl || '';
            if (!reviewUi || !productUrl) return;

            event.preventDefault();
            event.stopImmediatePropagation();
            window.location.assign(productUrl);
        };

        document.addEventListener('pointerdown', event => {
            const target = event.target.closest?.('button, a, input, select, textarea, [tabindex]:not([tabindex="-1"])');
            if (target instanceof HTMLElement && !target.closest('[data-modal]')) {
                lastInteractionElement = target;
            }
        });

        document.addEventListener('click', handleReviewNavigation, true);

        document.addEventListener('keydown', event => {
            const activeHost = getActiveHost();
            if (!activeHost) return;

            if (event.key === 'Escape') {
                event.preventDefault();
                closeAllActiveHosts();
                return;
            }

            activeHost.onDocumentKeydown(event);
        });
        document.addEventListener('click', event => {
            const reviewCount = event.target.closest?.('.pr-snippet-review-count');
            const reviewWrapper = reviewCount?.closest?.('[data-pr-product-url]');
            const productUrl = reviewWrapper?.dataset.prProductUrl || '';
            const reviewText = String(reviewCount?.textContent || '').trim().toLowerCase();

            if (reviewCount && productUrl && !reviewText.includes('no reviews')) {
                reviewCount.setAttribute('href', productUrl);
            }

            getActiveHost()?.onDocumentClick(event);
        });
        document.addEventListener('omni-quickview:open', event => {
            const targetSectionId = event.detail?.sectionId || '';
            const sectionHost = targetSectionId
                ? Array.from(hosts).find(
                    item => item.isConnected && item.sectionId === targetSectionId && item.modal
                )
                : null;
            const host = sectionHost || getGlobalHost() ||
                document.querySelector('omni-quickview[data-omni-quickview-modal]');

            if (host) host.onExternalQuickviewOpen(event);
        });
        window.addEventListener(UNIVERSAL_OPEN_EVENT, event => {
            const detail = event instanceof CustomEvent ? event.detail || {} : {};
            const host = getGlobalHost();

            if (!host) {
                dispatchUniversalError(
                    new Error('The global Omni Quick View host is not available.'),
                    detail
                );
                return;
            }

            host.openProduct(detail).catch(error => dispatchUniversalError(error, detail));
        });
        window.addEventListener('popstate', event => {
            getActiveHost()?.onPopState(event);
        });
        window.addEventListener('pagehide', () => {
            Array.from(hosts).forEach(host => host.resetForNavigation());
        });
        window.addEventListener('pageshow', () => {
            Array.from(hosts).forEach(host => host.resetForNavigation());
        });
        window.addEventListener('resize', () => {
            const activeHost = getActiveHost();
            activeHost?.updateVideoTogglePlacement();
            activeHost?.updatePurchaseActionState();
            activeHost?.updateMobilePurchaseActionPlacement();
        });
        window.visualViewport?.addEventListener('resize', () => getActiveHost()?.updateMobilePurchaseActionPlacement(), { passive: true });
        window.visualViewport?.addEventListener('scroll', () => getActiveHost()?.updateMobilePurchaseActionPlacement(), { passive: true });

        return {
            register(host) { hosts.add(host); },
            unregister(host) {
                hosts.delete(host);
                removeOpenedHost(host);
                syncDocumentOpenState();
            },
            noteOpened(host) {
                removeOpenedHost(host);
                openedHosts.push(host);
                document.documentElement.classList.add(QUICK_VIEW_OPEN_CLASS);
            },
            noteClosed(host) {
                removeOpenedHost(host);
                syncDocumentOpenState();
            },
            getLastInteractionElement() { return lastInteractionElement; },
            getGlobalHost
        };
    })();

    class OmniQuickview extends HTMLElement {
        constructor() {
            super();

            this.sectionId = '';
            this.colorOptionName = '';
            this.data = null;
            this.currentColor = '';
            this.currentPage = 1;
            this.allRenderedProducts = [];
            this.activeProduct = null;
            this.activeVariant = null;
            this.activeModalColor = '';
            this.abortController = null;
            this.galleryImages = [];
            this.galleryIndex = 0;
            this.galleryPointerStartX = null;
            this.modalScrollY = 0;
            this.modalScrollLockStyles = null;
            this.activePowerReviewsProductId = '';
            this.lastFocusedElement = null;
            this.lastInteractionElement = null;
            this.videoTogglePlacementFrame = null;
            this.purchaseActionFrame = null;
            this.pendingPowerReviewsProduct = null;
            this.initializationTimer = null;
            this.initializationAttempts = 0;
            this.productVisibilityObserver = null;
            this.productHydrationTimer = null;
            this.powerReviewsIdleCallback = null;
            this.powerReviewsIdleTimer = null;
            this.quickviewProductCache = new Map();
            this.universalOpenRequestId = 0;
            this.modalHistoryToken = '';
            this.modalHistoryEntryActive = false;
            this.lastModalBodyScrollTop = 0;
        }

        connectedCallback() {
            this.queueInitialization();
        }

        queueInitialization(delay = 0) {
            if (this.abortController || this.initializationTimer !== null) return;

            this.initializationTimer = window.setTimeout(() => {
                this.initializationTimer = null;
                if (this.isConnected) this.initialize();
            }, delay);
        }

        initialize() {
            if (this.abortController || !this.isConnected) return;

            this.sectionId = this.dataset.sectionId || '';
            this.colorOptionName = this.dataset.colorOption || '';

            if (!this.sectionId) return;

            // The global Quick View is a singleton. Some theme/app combinations
            // can render the global snippet twice, creating duplicate modal and
            // PowerReviews IDs. Leave only the first host active and remove the
            // conflicting markup before third-party scripts can target it.
            if (this.hasAttribute('data-omni-quickview-global')) {
                const primaryGlobalHost = document.querySelector(
                    'omni-quickview[data-omni-quickview-global]'
                );

                if (primaryGlobalHost && primaryGlobalHost !== this) {
                    this.dataset.omniQuickviewDuplicate = 'true';
                    this.hidden = true;
                    this.replaceChildren();
                    return;
                }
            }

            this.cacheElements();

            // A parser-created custom element connects before all of its children
            // necessarily exist. Do not permanently mark it initialized until its
            // quick-view markup has arrived.
            if (!this.modal && !this.grid) {
                this.initializationAttempts += 1;
                if (this.initializationAttempts < 5) this.queueInitialization(50);
                return;
            }

            this.abortController = new AbortController();

            try {
                this.currentColor = this.getSelectedPdpColor() || this.data?.currentColor || '';

                this.attachListeners();
                if (this.modal) {
                    this.dataset.omniQuickviewReady = 'true';
                }

                if (this.grid) this.deferProductGridHydration();
            } catch (error) {
                console.error('[OmniQuickview] Failed to initialize:', error);
                modalRuntime.unregister(this);
                this.abortController.abort();
                this.abortController = null;
                delete this.dataset.omniQuickviewReady;
                this.resetForNavigation();
            }
        }

        disconnectedCallback() {
            if (this.initializationTimer !== null) {
                window.clearTimeout(this.initializationTimer);
                this.initializationTimer = null;
            }

            this.resetForNavigation();
            modalRuntime.unregister(this);
            this.abortController?.abort();
            this.abortController = null;

            this.productVisibilityObserver?.disconnect();
            this.productVisibilityObserver = null;
            if (this.productHydrationTimer !== null) {
                window.clearTimeout(this.productHydrationTimer);
                this.productHydrationTimer = null;
            }
            if (this.powerReviewsIdleCallback !== null && 'cancelIdleCallback' in window) {
                window.cancelIdleCallback(this.powerReviewsIdleCallback);
            }
            this.powerReviewsIdleCallback = null;
            if (this.powerReviewsIdleTimer !== null) {
                window.clearTimeout(this.powerReviewsIdleTimer);
                this.powerReviewsIdleTimer = null;
            }

            if (this.videoTogglePlacementFrame) {
                cancelAnimationFrame(this.videoTogglePlacementFrame);
                this.videoTogglePlacementFrame = null;
            }

            if (this.purchaseActionFrame !== null) {
                cancelAnimationFrame(this.purchaseActionFrame);
                this.purchaseActionFrame = null;
            }

            if (this.modal?.parentElement === document.body) {
                this.appendChild(this.modal);
            }
        }

        cacheElements() {
            this.grid = this.querySelector('[data-product-grid]');
            this.fallback = this.querySelector('[data-fallback]');
            this.pagination = this.querySelector('[data-pagination]');
            this.pageStatus = this.querySelector('[data-page-status]');
            this.prevBtn = this.querySelector('[data-prev]');
            this.nextBtn = this.querySelector('[data-next]');
            this.modal = this.querySelector('[data-modal]');
            this.modalBody = this.querySelector('[data-modal-body]');
            this.modalClose = this.querySelector('[data-modal-close]');
            this.gallery = this.querySelector('[data-gallery]');
            this.galleryTrack = this.querySelector('[data-gallery-track]');
            this.galleryPrev = this.querySelector('[data-gallery-prev]');
            this.galleryNext = this.querySelector('[data-gallery-next]');
            this.galleryDots = this.querySelector('[data-gallery-dots]');
            this.modalImg = this.querySelector('[data-modal-img]');
            this.modalVideo = this.querySelector('[data-modal-video]');
            this.videoToggle = this.querySelector('[data-video-toggle]');
            this.modalTitle = this.querySelector('[data-modal-title]');
            this.modalPrice = this.querySelector('[data-modal-price]');
            this.powerReviewsWrapper = this.querySelector('[data-powerreviews-wrapper]');
            this.powerReviewsSnippet = this.querySelector('[data-powerreviews-snippet]');
            this.modalDescription = this.querySelector('[data-modal-description]');
            this.variantSelector = this.querySelector('[data-variant-selector]');
            this.errorDiv = this.querySelector('[data-error]');
            this.loading = this.querySelector('[data-loading]');
            this.addToCartBtn = this.querySelector('[data-add-to-cart]');
            this.addToCartButtons = Array.from(this.querySelectorAll('[data-add-to-cart], [data-add-to-cart-sticky]'));
            this.mobileCartAction = this.querySelector('[data-mobile-cart-action]');
            this.productLink = this.querySelector('[data-product-link]');
            this.customizeWrapper = this.querySelector('[data-customize-wrapper]');

        }

        moveModalToBody() {
            if (this.hasAttribute('data-omni-quickview-global')) return;

            if (this.modal && this.modal.parentElement !== document.body) {
                document.body.appendChild(this.modal);
            }
        }

        restoreModalToHost() {
            if (this.hasAttribute('data-omni-quickview-global')) return;

            if (this.modal?.parentElement === document.body) {
                this.appendChild(this.modal);
            }
        }

        resetForNavigation() {
            this.modal?.classList.remove('active');
            this.modalVideo?.pause();
            modalRuntime.noteClosed(this);
            this.modalHistoryEntryActive = false;
            this.modalHistoryToken = '';
            this.unlockPageScroll(false);
            this.restoreModalToHost();
            this.lastFocusedElement = null;
            this.lastInteractionElement = null;
        }

        loadQuickviewData() {
            const dataElement = document.getElementById(`quickview-data-${this.sectionId}`);
            if (!dataElement) return false;

            try {
                this.data = JSON.parse(dataElement.textContent || '{}');
                return true;
            } catch (error) {
                console.error('Failed to parse quickview data:', error);
                return false;
            }
        }

        deferProductGridHydration() {
            const hydrate = () => {
                this.productHydrationTimer = null;
                this.productVisibilityObserver?.disconnect();
                this.productVisibilityObserver = null;
                if (!this.isConnected || !this.grid) return;

                if (!this.loadQuickviewData()) {
                    this.queueQuickviewDataLoad();
                    return;
                }

                this.currentColor = this.getSelectedPdpColor() || this.data?.currentColor || this.currentColor || '';
                if (this.data?.products) this.renderProducts(this.currentColor);
            };

            if (!('IntersectionObserver' in window)) {
                this.productHydrationTimer = window.setTimeout(hydrate, 0);
                return;
            }

            this.productVisibilityObserver = new IntersectionObserver(entries => {
                if (entries.some(entry => entry.isIntersecting)) hydrate();
            }, { rootMargin: '800px 0px' });
            this.productVisibilityObserver.observe(this);
        }

        queueQuickviewDataLoad() {
            [0, 50, 150, 350].forEach(delay => {
                window.setTimeout(() => {
                    if (!this.isConnected) return;
                    if (this.data?.products) return;
                    if (!this.loadQuickviewData()) return;

                    this.currentColor = this.getSelectedPdpColor() || this.data?.currentColor || '';
                    if (this.data?.products) {
                        this.renderProducts(this.currentColor);
                    }
                }, delay);
            });
        }

        lockPageScroll() {
            if (this.modalScrollLockStyles) return;

            const body = document.body;
            const html = document.documentElement;
            const scrollbarWidth = window.innerWidth - html.clientWidth;

            this.modalScrollY = window.pageYOffset || html.scrollTop || body.scrollTop || 0;
            this.modalScrollLockStyles = {
                bodyOverflow: body.style.overflow,
                bodyPaddingRight: body.style.paddingRight,
                bodyScrollBehavior: body.style.scrollBehavior,
                bodyOverscrollBehavior: body.style.overscrollBehavior,
                htmlOverflow: html.style.overflow,
                htmlScrollBehavior: html.style.scrollBehavior,
                htmlScrollbarGutter: html.style.scrollbarGutter
            };

            body.classList.add('curated-looks-modal-open');
            if (scrollbarWidth > 0) {
                const currentPadding = parseFloat(body.style.paddingRight) || 0;
                body.style.paddingRight = `${currentPadding + scrollbarWidth}px`;
            }

            body.style.overflow = 'hidden';
            body.style.scrollBehavior = 'auto';
            body.style.overscrollBehavior = 'contain';
            html.style.overflow = 'hidden';
            html.style.scrollBehavior = 'auto';
            html.style.scrollbarGutter = 'stable';
            window.scrollTo(0, this.modalScrollY);
        }

        unlockPageScroll(restoreScroll = true) {
            if (!this.modalScrollLockStyles) return;

            const body = document.body;
            const html = document.documentElement;
            const scrollY = this.modalScrollY || 0;
            const previousStyles = this.modalScrollLockStyles;

            body.classList.remove('curated-looks-modal-open');
            body.style.overflow = previousStyles.bodyOverflow;
            body.style.paddingRight = previousStyles.bodyPaddingRight;
            body.style.scrollBehavior = previousStyles.bodyScrollBehavior;
            body.style.overscrollBehavior = previousStyles.bodyOverscrollBehavior;
            html.style.overflow = previousStyles.htmlOverflow;
            html.style.scrollBehavior = previousStyles.htmlScrollBehavior;
            html.style.scrollbarGutter = previousStyles.htmlScrollbarGutter;

            this.modalScrollLockStyles = null;
            if (restoreScroll) window.scrollTo(0, scrollY);
        }

        attachListeners() {
            const { signal } = this.abortController;

            if (this.grid) {
                this.grid.addEventListener('click', event => this.onGridClick(event), { signal });
                this.grid.addEventListener('pointerover', event => this.onGridIntent(event), { signal, passive: true });
                this.grid.addEventListener('focusin', event => this.onGridIntent(event), { signal });
                this.grid.addEventListener('touchstart', event => this.onGridIntent(event), { signal, passive: true });
                this.grid.addEventListener('scroll', () => {
                    if (this.isMobile()) this.updateMobilePaginationFromScroll();
                }, { signal });
                this.addEventListener('click', event => this.onRootClick(event), { signal });

                document.addEventListener('pdp:color-change', event => {
                    const color = event.detail?.color;
                    if (!color || this.normalizeValue(color) === this.normalizeValue(this.currentColor)) return;
                    this.updateColor(color);
                }, { signal });
            }

            if (this.modal) {
                modalRuntime.register(this);
                this.modal.addEventListener('click', event => this.onModalClick(event), { signal });
                this.modal.addEventListener('input', event => this.onModalInput(event), { signal });
                this.modal.addEventListener('change', event => this.onModalQuantityChange(event), { signal });
                this.modalBody?.addEventListener('scroll', () => this.onModalBodyScroll(), { signal, passive: true });
            }
        }

        onModalBodyScroll() {
            this.updateMobilePurchaseActionPlacement();
        }

        onDocumentKeydown(event) {
            if (!this.modal?.classList.contains('active')) return;

            if (event.key === 'Escape') {
                event.preventDefault();
                this.closeModal();
                return;
            }

            if (this.handleColorButtonKeydown(event)) return;

            if (event.key === 'Tab') {
                this.trapModalFocus(event);
            }
        }

        handleColorButtonKeydown(event) {
            const colorButton = event.target.closest?.('[data-color-value]');
            if (!colorButton || !this.modal?.contains(colorButton)) return false;

            const navigationKeys = ['ArrowLeft', 'ArrowUp', 'ArrowRight', 'ArrowDown', 'Home', 'End'];
            if (!navigationKeys.includes(event.key)) return false;

            const colorButtons = Array.from(this.modal.querySelectorAll('[data-color-value]'));
            const currentIndex = colorButtons.indexOf(colorButton);
            if (currentIndex === -1) return false;

            event.preventDefault();

            let nextIndex = currentIndex;
            if (event.key === 'Home') nextIndex = 0;
            if (event.key === 'End') nextIndex = colorButtons.length - 1;
            if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
                nextIndex = (currentIndex - 1 + colorButtons.length) % colorButtons.length;
            }
            if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
                nextIndex = (currentIndex + 1) % colorButtons.length;
            }

            const nextButton = colorButtons[nextIndex];
            nextButton?.focus({ preventScroll: true });
            event.stopPropagation();
            this.selectModalColor(nextButton?.dataset.colorValue || '');
            return true;
        }

        storeInteractionElement(event) {
            if (this.modal?.contains(event.target)) return;

            const target = event.target.closest?.('button, a, input, select, textarea, [tabindex]:not([tabindex="-1"])');
            if (target instanceof HTMLElement) {
                this.lastInteractionElement = target;
            }
        }

        getFocusableModalElements() {
            if (!this.modal) return [];

            return Array.from(this.modal.querySelectorAll([
                'a[href]',
                'button:not([disabled])',
                'input:not([disabled])',
                'select:not([disabled])',
                'textarea:not([disabled])',
                '[tabindex]:not([tabindex="-1"])'
            ].join(','))).filter(element => {
                return !element.hidden && !!(element.offsetWidth || element.offsetHeight || element.getClientRects().length);
            });
        }

        focusModal() {
            if (!this.modal) return;

            const focusTarget =
                this.modal.querySelector('[data-modal-close]') ||
                this.modal.querySelector('[data-modal-title]') ||
                this.getFocusableModalElements()[0] ||
                this.modal;

            window.setTimeout(() => focusTarget?.focus?.({ preventScroll: true }), 0);
        }

        restoreOpenerFocus() {
            const focusTarget = this.lastFocusedElement;
            this.lastFocusedElement = null;
            this.lastInteractionElement = null;

            if (!focusTarget || !document.contains(focusTarget)) return;

            window.setTimeout(() => focusTarget.focus?.({ preventScroll: true }), 0);
        }

        trapModalFocus(event) {
            const focusableElements = this.getFocusableModalElements();

            if (!focusableElements.length) {
                event.preventDefault();
                this.modal?.focus?.({ preventScroll: true });
                return;
            }

            const firstElement = focusableElements[0];
            const lastElement = focusableElements[focusableElements.length - 1];
            const activeElement = document.activeElement;

            if (event.shiftKey && activeElement === firstElement) {
                event.preventDefault();
                lastElement.focus({ preventScroll: true });
                return;
            }

            if (!event.shiftKey && activeElement === lastElement) {
                event.preventDefault();
                firstElement.focus({ preventScroll: true });
            }
        }

        onRootClick(event) {
            const prev = event.target.closest('[data-prev]');
            const next = event.target.closest('[data-next]');

            if (prev) {
                event.preventDefault();
                this.goToPreviousPage();
            }

            if (next) {
                event.preventDefault();
                this.goToNextPage();
            }
        }

        onGridIntent(event) {
            const card = event.target.closest?.('[data-curated-looks-product-handle]');
            if (!card || !this.data?.products) return;

            const product = this.data.products.find(
                item => item.handle === card.dataset.curatedLooksProductHandle
            );
            if (product) this.fetchQuickviewProduct(product);
        }

        async fetchQuickviewProduct(product) {
            const cacheKey = product?.handle || product?.url || '';
            if (!cacheKey || !product?.url) return null;
            if (this.quickviewProductCache.has(cacheKey)) {
                return this.quickviewProductCache.get(cacheKey);
            }

            const request = this.fetchQuickviewProductWithFallbacks(product)
                .then(fullProduct => {
                    if (fullProduct) {
                        fullProduct.source = product.source || 'curated-looks';
                        this.quickviewProductCache.set(cacheKey, fullProduct);
                    } else {
                        this.quickviewProductCache.delete(cacheKey);
                    }
                    return fullProduct;
                });

            this.quickviewProductCache.set(cacheKey, request);
            return request;
        }

        /**
         * Public universal Quick View entry point.
         *
         * @param {string | { productUrl?: string, url?: string, variantId?: string | number, source?: string }} productUrlOrOptions
         * @param {string | number} [suppliedVariantId]
         * @returns {Promise<boolean>}
         */
        async openProduct(productUrlOrOptions, suppliedVariantId) {
            const request = this.normalizeUniversalOpenRequest(productUrlOrOptions, suppliedVariantId);
            const requestId = ++this.universalOpenRequestId;

            if (!this.abortController) {
                this.initialize();
            }
            if (!this.modal) {
                this.cacheElements();
            }
            if (!this.modal) {
                throw new Error('The global Omni Quick View modal markup is unavailable.');
            }

            this.dataset.omniQuickviewLoading = 'true';

            try {
                const product = await this.fetchQuickviewProduct({
                    handle: request.handle,
                    url: request.productUrl,
                    source: request.source
                });

                if (requestId !== this.universalOpenRequestId) return false;
                if (!product?.variants?.length) {
                    throw new Error(`Unable to load Quick View product data for ${request.productUrl}.`);
                }

                const selectedVariant = request.variantId
                    ? product.variants.find(variant => String(variant.id) === request.variantId)
                    : product.variants.find(variant => variant.available) || product.variants[0];

                if (!selectedVariant) {
                    throw new Error(`Variant ${request.variantId} was not found for ${request.productUrl}.`);
                }

                product.source = request.source;
                const productLink = new URL(product.url || request.productUrl, window.location.origin);
                if (request.variantId) productLink.searchParams.set('variant', request.variantId);
                product.url = productLink.toString();

                const opened = this.openModal(product, selectedVariant);
                if (!opened) {
                    throw new Error('The global Omni Quick View modal could not be opened.');
                }

                window.dispatchEvent(new CustomEvent(UNIVERSAL_OPENED_EVENT, {
                    detail: {
                        productUrl: product.url,
                        productId: String(product.id || ''),
                        variantId: String(selectedVariant.id || '')
                    }
                }));

                return true;
            } finally {
                if (requestId === this.universalOpenRequestId) {
                    delete this.dataset.omniQuickviewLoading;
                }
            }
        }

        normalizeUniversalOpenRequest(productUrlOrOptions, suppliedVariantId) {
            const options =
                typeof productUrlOrOptions === 'object' && productUrlOrOptions !== null
                    ? productUrlOrOptions
                    : { productUrl: productUrlOrOptions, variantId: suppliedVariantId };
            const rawProductUrl = options.productUrl || options.url || '';

            if (typeof rawProductUrl !== 'string' || !rawProductUrl.trim()) {
                throw new TypeError('Omni Quick View requires a productUrl.');
            }

            const productUrl = new URL(rawProductUrl, window.location.origin);
            if (productUrl.origin !== window.location.origin) {
                throw new TypeError('Omni Quick View only accepts same-origin product URLs.');
            }
            if (!/(^|\/)products\//.test(productUrl.pathname)) {
                throw new TypeError('Omni Quick View requires a Shopify product URL.');
            }

            const requestedVariantId =
                options.variantId ?? productUrl.searchParams.get('variant') ?? '';
            const variantId = requestedVariantId === ''
                ? ''
                : String(requestedVariantId).trim().split('/').pop() || '';

            if (variantId && !/^\d+$/.test(variantId)) {
                throw new TypeError('Omni Quick View variantId must be a numeric Shopify variant ID.');
            }

            productUrl.search = '';
            productUrl.hash = '';

            const pathParts = productUrl.pathname.split('/').filter(Boolean);
            const handle = decodeURIComponent(pathParts[pathParts.length - 1] || '');

            return {
                productUrl: productUrl.toString(),
                variantId,
                handle,
                source: String(options.source || 'external')
            };
        }

        parseQuickviewProductHtml(html) {
            if (!html) return null;
            const doc = new DOMParser().parseFromString(html, 'text/html');
            const dataElement = doc.querySelector('[data-omni-quickview-product-json]');
            if (!dataElement) return null;

            try {
                return JSON.parse(dataElement.textContent || '{}');
            } catch (error) {
                return null;
            }
        }

        async fetchQuickviewProductWithFallbacks(product) {
            const productUrl = new URL(product.url, window.location.origin);
            productUrl.search = '';
            productUrl.hash = '';

            try {
                const sectionUrl = new URL(productUrl);
                sectionUrl.searchParams.set('section_id', 'omni-quickview-product-data');
                const response = await fetch(sectionUrl.toString(), {
                    headers: { Accept: 'text/html' }
                });
                if (response.ok) {
                    const sectionProduct = this.parseQuickviewProductHtml(await response.text());
                    if (sectionProduct) return sectionProduct;
                }
            } catch (error) {
                // Continue through the progressively more generic fallbacks below.
            }

            try {
                const response = await fetch(productUrl.toString(), {
                    headers: { Accept: 'text/html' }
                });
                if (response.ok) {
                    const pageProduct = this.parseQuickviewProductHtml(await response.text());
                    if (pageProduct) return pageProduct;
                }
            } catch (error) {
                // Continue to Shopify's standard product JSON endpoint.
            }

            try {
                const jsonUrl = new URL(productUrl);
                jsonUrl.pathname = `${jsonUrl.pathname.replace(/\/$/, '')}.js`;
                const response = await fetch(jsonUrl.toString(), {
                    headers: { Accept: 'application/json' }
                });
                if (!response.ok) throw new Error('Unable to fetch Shopify product JSON');
                return this.buildQuickviewProductFromShopifyJson(await response.json(), product);
            } catch (error) {
                console.error('[OmniQuickview] Failed to load product data after all fallbacks:', error);
                return null;
            }
        }

        buildQuickviewProductFromShopifyJson(rawProduct, lightweightProduct = {}) {
            if (!rawProduct?.variants?.length) return null;

            const options = (rawProduct.options || []).map((option, index) => ({
                name: typeof option === 'string' ? option : option.name,
                position: typeof option === 'string' ? index + 1 : option.position || index + 1,
                values: (typeof option === 'string' ? [] : option.values || []).map(value => ({
                    name: typeof value === 'string' ? value : value.name || '',
                    swatchHtml: ''
                }))
            }));
            const optionNames = options.map(option => this.normalizeValue(option.name));
            const colorIndex = optionNames.findIndex(name => name === 'color' || name === 'colour');
            const sizeIndex = optionNames.findIndex(name => name === 'size');
            const featuredImage = rawProduct.featured_image?.src || rawProduct.featured_image || lightweightProduct.image || '';

            return {
                source: lightweightProduct.source || 'curated-looks',
                id: rawProduct.id || lightweightProduct.id,
                handle: rawProduct.handle || lightweightProduct.handle,
                title: rawProduct.title || lightweightProduct.title,
                productType: rawProduct.type || lightweightProduct.productType || '',
                tags: rawProduct.tags || lightweightProduct.tags || [],
                url: rawProduct.url || lightweightProduct.url,
                image: featuredImage,
                description: rawProduct.description || '',
                options,
                variants: rawProduct.variants.map(variant => {
                    const variantOptions = variant.options || [variant.option1, variant.option2, variant.option3].filter(Boolean);
                    return {
                        id: variant.id,
                        title: variant.title || '',
                        price: variant.price,
                        compareAtPrice: variant.compare_at_price,
                        available: variant.available,
                        options: variantOptions,
                        color: colorIndex >= 0 ? variantOptions[colorIndex] || '' : '',
                        size: sizeIndex >= 0 ? variantOptions[sizeIndex] || '' : '',
                        image: variant.featured_image?.src || variant.featured_image || featuredImage,
                        imagery: []
                    };
                })
            };
        }

        async onGridClick(event) {
            const card = event.target.closest('[data-curated-looks-product-handle]');
            if (!card || !this.data?.products) return;

            event.preventDefault();

            const product = this.data.products.find(
                item => item.handle === card.dataset.curatedLooksProductHandle
            );
            const lightweightVariant = product?.variants.find(
                item => String(item.id) === String(card.dataset.curatedLooksVariantId)
            );

            if (!product || !lightweightVariant) return;

            card.setAttribute('aria-busy', 'true');
            const fullProduct = await this.fetchQuickviewProduct(product);
            card.removeAttribute('aria-busy');
            if (!fullProduct || !this.isConnected) return;

            const variant = fullProduct.variants?.find(
                item => String(item.id) === String(lightweightVariant.id)
            ) || fullProduct.variants?.find(item => item.available) || fullProduct.variants?.[0];
            if (!variant) return;

            if (this.openModal(fullProduct, variant)) return;

            document.dispatchEvent(new CustomEvent('omni-quickview:open', {
                detail: {
                    sectionId: this.sectionId,
                    product: fullProduct,
                    variant
                }
            }));
        }

        onModalClick(event) {
            if (event.target === this.modal || event.target.closest('[data-modal-close]')) {
                event.preventDefault();
                this.closeModal();
                return;
            }

            if (event.defaultPrevented) return;

            const addToCart = event.target.closest('[data-add-to-cart], [data-add-to-cart-sticky]');
            if (addToCart) {
                event.preventDefault();
                event.stopPropagation();

                if (addToCart.dataset.soldOutContinue === 'true') {
                    this.closeModal();
                    return;
                }

                if (this.getSelectedQuantityTotal() < 1) {
                    this.focusQuantitySelector();
                    return;
                }

                this.addSelectedSizesToCart();
                return;
            }

            const productLink = event.target.closest('[data-product-link]');
            if (productLink) {
                const color = this.activeModalColor || this.activeVariant?.color || '';
                if (color) this.setPdpColorCookie(color);

                if (this.hasCurrentModalHistoryEntry()) {
                    event.preventDefault();
                    this.navigateFromModal(productLink.href);
                }
                return;
            }

            const customizeBtn = event.target.closest('[data-product-url]');
            if (customizeBtn?.dataset.productUrl) {
                event.preventDefault();

                if (customizeBtn.dataset.directProductLink === 'true') {
                    this.navigateFromModal(customizeBtn.dataset.productUrl);
                    return;
                }

                const color = this.activeModalColor || this.activeVariant?.color || '';
                if (color) this.setPdpColorCookie(color);

                const productUrl = this.buildCustomizerPdpUrl(customizeBtn.dataset.productUrl);

                this.navigateFromModal(productUrl.toString());
                return;
            }

            const colorButton = event.target.closest('[data-color-value]');
            if (colorButton) {
                event.preventDefault();
                // Searchspring and the theme both use delegated swatch handlers.
                // Keep Quick View color changes inside the modal.
                event.stopPropagation();
                this.selectModalColor(colorButton.dataset.colorValue || '');
                return;
            }

            const previousImage = event.target.closest('[data-gallery-prev]');
            if (previousImage) {
                event.preventDefault();
                this.goToGalleryImage(this.galleryIndex - 1);
                return;
            }

            const nextImage = event.target.closest('[data-gallery-next]');
            if (nextImage) {
                event.preventDefault();
                this.goToGalleryImage(this.galleryIndex + 1);
                return;
            }

            const galleryDot = event.target.closest('[data-gallery-index]');
            if (galleryDot) {
                event.preventDefault();
                this.goToGalleryImage(Number(galleryDot.dataset.galleryIndex || 0));
                return;
            }

            const videoToggle = event.target.closest('[data-video-toggle]');
            if (videoToggle) {
                event.preventDefault();
                this.toggleModalVideo();
            }
        }

        onModalInput(event) {
            if (!event.target.matches?.(`.curated-looks-size-qty-${this.sectionId}`)) return;

            // Searchspring owns PLP/SERP controls with document-level input
            // listeners. Keep QV quantities inside the modal so they cannot be
            // mistaken for a Searchspring filter and trigger a results rerender.
            event.stopPropagation();

            this.schedulePurchaseActionStateUpdate();

            if (this.getSelectedQuantityTotal() > 0) {
                if (this.errorDiv?.classList.contains('active')) {
                    this.errorDiv.classList.remove('active');
                }
                if (this.errorDiv?.textContent) this.errorDiv.textContent = '';
            }
        }

        onModalQuantityChange(event) {
            if (!event.target.matches?.(`.curated-looks-size-qty-${this.sectionId}`)) return;

            // Browsers fire change when the quantity field is committed. Block
            // that companion event for the same reason as the input event above.
            event.stopPropagation();
            this.schedulePurchaseActionStateUpdate();
        }

        onDocumentChange(event) {
            const colorInput = event.target.closest('input[type="radio"][name^="color-selector-"]');
            if (!colorInput || this.modal?.contains(colorInput)) return;

            const color = colorInput.dataset.colorName || colorInput.value || '';
            if (color) {
                this.setPdpColorCookie(color);
                this.updateColor(color);
                document.dispatchEvent(new CustomEvent('pdp:color-change', {
                    detail: { color }
                }));
            }

            this.queuePdpColorSync();
        }

        onDocumentClick(event) {
            const reviewCount = event.target.closest('.pr-snippet-review-count');
            if (reviewCount) {
                const reviewWrapper = reviewCount.closest('[data-pr-product-url]');
                const productUrl = reviewWrapper?.dataset.prProductUrl || '';
                const text = String(reviewCount.textContent || '').trim().toLowerCase();

                if (productUrl && !text.includes('no reviews')) {
                    reviewCount.setAttribute('href', productUrl);
                    return;
                }
            }

            if (this.modal?.contains(event.target)) return;

            const label = event.target.closest('label[for^="color-"]');
            const colorOption = event.target.closest('.color-option');
            if (!label && !colorOption) return;

            this.queuePdpColorSync();
        }

        onExternalQuickviewOpen(event) {
            const targetSectionId = event.detail?.sectionId || '';
            const isGlobalHost = this.hasAttribute('data-omni-quickview-global');

            if (!this.abortController) this.initialize();

            if (!isGlobalHost && targetSectionId && targetSectionId !== this.sectionId) return;
            if (!isGlobalHost && !targetSectionId) {
                const modalQuickview =
                    document.querySelector('omni-quickview[data-omni-quickview-modal]') ||
                    document.querySelector('omni-quickview');

                if (modalQuickview !== this) return;
            }

            const product = event.detail?.product;
            const selectedVariant = event.detail?.variant || product?.variants?.[0];

            if (!product || !selectedVariant) return;

            this.openModal(product, selectedVariant);
        }

        queuePdpColorSync() {
            const updateFromPdp = () => {
                const color = this.getSelectedPdpColor();
                if (!color) return;

                this.setPdpColorCookie(color);
                this.updateColor(color);
            };

            setTimeout(updateFromPdp, 0);
            setTimeout(updateFromPdp, 100);
        }

        goToPreviousPage() {
            if (this.isMobile()) {
                this.scrollMobilePage(-1);
                return;
            }

            if (this.currentPage > 1) {
                this.currentPage -= 1;
                this.renderCurrentPage();
            }
        }

        goToNextPage() {
            if (this.isMobile()) {
                this.scrollMobilePage(1);
                return;
            }

            const totalPages = this.getTotalPages();
            if (this.currentPage < totalPages) {
                this.currentPage += 1;
                this.renderCurrentPage();
            }
        }

        scrollMobilePage(direction) {
            const card = this.grid?.querySelector(`.curated-looks__card-${this.sectionId}`);
            if (!card || !this.grid) return;

            const gap = 12;
            const scrollAmount = card.offsetWidth + gap;
            const totalPages = this.getTotalPages();

            this.currentPage = Math.min(
                totalPages,
                Math.max(1, this.currentPage + direction)
            );

            this.grid.scrollBy({
                left: scrollAmount * direction,
                behavior: 'smooth'
            });

            this.renderPaginationState();
        }

        openModal(product, selectedVariant) {
            if (!this.modal) {
                this.cacheElements();
                this.moveModalToBody();
            }

            if (this.modal) {
                this.dataset.omniQuickviewReady = 'true';
            }

            if (!this.modal) return false;

            const wasModalActive = this.modal.classList.contains('active');
            try {
                if (!wasModalActive) {
                    this.moveModalToBody();
                    modalRuntime.noteOpened(this);
                    this.lastFocusedElement =
                        modalRuntime.getLastInteractionElement() ||
                        this.lastInteractionElement ||
                        (document.activeElement instanceof HTMLElement ? document.activeElement : null);
                    this.lockPageScroll();
                    this.pushModalHistoryEntry();
                }

                this.activeProduct = product;
                this.activeVariant = selectedVariant;
                this.activeModalColor = selectedVariant.color || '';
                this.activeContactOnly = this.isContactOnlyProduct(product);
                this.activePackageProduct = this.isPackageProduct(product);
                this.activeCustomizeButtonConfig = this.getCustomizeButtonConfigForProduct(product);
                if (this.activeCustomizeButtonConfig) {
                    this.warmCustomizerExperience(product);
                }
                this.setPurchaseActionsHidden(
                    this.activePackageProduct ||
                    this.activeContactOnly ||
                    this.activeCustomizeButtonConfig?.customizeOnly === true
                );
                this.activeModalSingleSize = this.isSingleSizeProduct(product);

                this.modal.dataset.quickviewSource = product.source || 'runtime';

                if (this.modalImg) {
                    this.modalImg.src = selectedVariant.image || product.image || '';
                    this.modalImg.alt = product.title || '';
                }

                if (this.modalTitle) this.modalTitle.textContent = product.title || '';
                this.renderModalPrice(selectedVariant, product, { deferReviews: true });
                this.renderModalDescription(product.description || '');
                this.syncModalProductLinkVariant(product.url || '', selectedVariant.id);

                this.renderModalGallery(product, selectedVariant);

                if (this.variantSelector) this.variantSelector.innerHTML = '';
                if (this.customizeWrapper) this.customizeWrapper.innerHTML = '';
                if (this.modalBody) this.modalBody.scrollTop = 0;
                this.lastModalBodyScrollTop = 0;
                this.updatePurchaseActionState();

                this.modal.classList.add('active');

                requestAnimationFrame(() => {
                    if (!this.modal?.classList.contains('active')) return;
                    try {
                        this.renderModalOptions(product, selectedVariant);
                        this.renderCustomizeAction(product);
                        this.scheduleVideoTogglePlacement();
                        this.focusModal();
                        this.updateMobilePurchaseActionPlacement();
                        this.renderPendingModalPowerReviews();
                    } catch (error) {
                        console.error('[OmniQuickview] Failed to render modal:', error);
                        this.closeModal({ restoreFocus: false });
                    }
                });

                return true;
            } catch (error) {
                console.error('[OmniQuickview] Failed to open modal:', error);
                this.closeModal({ restoreFocus: false });
                return false;
            }
        }

        closeModal(options = {}) {
            if (!this.modal) return;

            const wasOpen = this.modal.classList.contains('active') || !!this.modalScrollLockStyles;
            if (!wasOpen) return;

            const shouldConsumeHistoryEntry = options.fromHistory !== true &&
                this.hasCurrentModalHistoryEntry();

            this.modalHistoryEntryActive = false;
            this.modalHistoryToken = '';

            this.modal.classList.remove('active');
            this.mobileCartAction?.classList.remove('is-floating');
            modalRuntime.noteClosed(this);
            this.modalVideo?.pause();
            this.unlockPageScroll(options.restoreScroll !== false);
            this.restoreModalToHost();
            if (options.restoreFocus !== false) {
                this.restoreOpenerFocus();
            }

            if (shouldConsumeHistoryEntry) {
                window.history.back();
            }
        }

        hasCurrentModalHistoryEntry() {
            return this.modalHistoryEntryActive &&
                window.history.state?.[MODAL_HISTORY_STATE_KEY] === this.modalHistoryToken;
        }

        navigateFromModal(url) {
            const replaceModalHistoryEntry = this.hasCurrentModalHistoryEntry();

            this.closeModal({
                fromHistory: replaceModalHistoryEntry,
                restoreFocus: false,
                restoreScroll: false
            });

            if (replaceModalHistoryEntry) {
                window.location.replace(url);
            } else {
                window.location.assign(url);
            }
        }

        onPopState() {
            if (!this.modal?.classList.contains('active') && !this.modalScrollLockStyles) return;

            this.modalHistoryEntryActive = false;

            this.closeModal({
                fromHistory: true,
                restoreFocus: false,
                restoreScroll: false
            });
        }

        pushModalHistoryEntry() {
            if (!this.isMobile() || this.modalHistoryEntryActive) return;

            this.modalHistoryToken = `${this.sectionId}:${Date.now()}:${Math.random().toString(36).slice(2)}`;

            const currentState = window.history.state;
            const nextState = currentState && typeof currentState === 'object'
                ? { ...currentState }
                : {};
            nextState[MODAL_HISTORY_STATE_KEY] = this.modalHistoryToken;

            try {
                window.history.pushState(nextState, '', window.location.href);
                this.modalHistoryEntryActive = true;
            } catch (error) {
                this.modalHistoryToken = '';
                console.warn('[OmniQuickview] Unable to create modal history entry:', error);
            }
        }

        renderCustomizeAction(product, options = {}) {
            if (!this.customizeWrapper) return;

            const buttonConfig = this.activeCustomizeButtonConfig || this.getCustomizeButtonConfigForProduct(product);
            const isCustomizeOnly = buttonConfig?.customizeOnly === true;
            const isContactOnly = this.activeContactOnly || this.isContactOnlyProduct(product);
            const isPackage = this.activePackageProduct || this.isPackageProduct(product);
            const hideSoldOutCustomizeAction = Boolean(buttonConfig) && this.isActiveQuickViewSelectionSoldOut(product);

            this.setPurchaseActionsHidden(
                isPackage || isContactOnly || (isCustomizeOnly && !hideSoldOutCustomizeAction)
            );

            if (isPackage) {
                this.customizeWrapper.removeAttribute('style');
                this.customizeWrapper.innerHTML = `
          <button
            type="button"
            class="curated-looks-customize-btn-${this.sectionId} build-package-button"
            data-product-url="${this.escapeAttribute(product.url || '')}"
            data-direct-product-link="true"
          >
            Build this Package
          </button>
        `;
                return;
            }

            if (isContactOnly) {
                Object.assign(this.customizeWrapper.style, {
                    display: 'block',
                    width: '100%'
                });

                this.customizeWrapper.innerHTML = `
          <a
            class="curated-looks-customize-btn-${this.sectionId} contact-us-button"
            href="${this.escapeAttribute(this.getContactUrl(product))}"
            style="font-weight: 500; display: flex; align-items: center; justify-content: center; width: 100%; box-sizing: border-box; text-align: center; text-decoration: none;"
          >
            Contact Us to Order
          </a>
        `;
                return;
            }

            if (hideSoldOutCustomizeAction) {
                this.customizeWrapper.removeAttribute('style');
                this.customizeWrapper.innerHTML = '';
                return;
            }

            if (buttonConfig) {
                if (options.warmCustomizer !== false) {
                    this.warmCustomizerExperience(product);
                }
                this.customizeWrapper.removeAttribute('style');

                this.customizeWrapper.innerHTML = `
          <button
            type="button"
            class="curated-looks-customize-btn-${this.sectionId} ${buttonConfig.className}"
            data-product-url="${this.escapeAttribute(product.url || '')}"
            data-customization-type="${this.escapeAttribute(buttonConfig.customizationType)}"
          >
            ${buttonConfig.label}
          </button>
        `;
                return;
            }

            this.customizeWrapper.removeAttribute('style');
            this.customizeWrapper.innerHTML = '';
        }

        buildCustomizerPdpUrl(rawProductUrl) {
            const productUrl = new URL(rawProductUrl, window.location.origin);

            if (this.activeVariant?.id) {
                productUrl.searchParams.set('variant', String(this.activeVariant.id));
            }

            productUrl.searchParams.set('customize', 'true');
            productUrl.searchParams.set(PDP_CUSTOMIZER_LAUNCH_PARAM, '1');
            return productUrl;
        }

        syncModalProductLinkVariant(rawProductUrl, variantId) {
            if (!this.productLink || !rawProductUrl) return;

            try {
                const productUrl = new URL(rawProductUrl, window.location.origin);
                if (variantId) productUrl.searchParams.set('variant', String(variantId));
                else productUrl.searchParams.delete('variant');
                this.productLink.href = productUrl.toString();
            } catch (error) {
                this.productLink.href = rawProductUrl;
            }
        }

        ensureResourceHint(rel, href) {
            const normalizedHref = new URL(href, window.location.origin).href;
            const existingHint = Array.from(document.querySelectorAll(`link[rel="${rel}"]`))
                .find(link => link.href === normalizedHref);

            if (existingHint) return existingHint;

            const hint = document.createElement('link');
            hint.rel = rel;
            hint.href = normalizedHref;
            document.head.appendChild(hint);
            return hint;
        }

        warmCustomizerExperience(product) {
            if (!product?.url) return;

            let productUrl;
            try {
                productUrl = this.buildCustomizerPdpUrl(product.url);
            } catch (error) {
                console.warn('[OmniQuickview] Could not warm customizer destination:', error);
                return;
            }

            if (productUrl.origin !== window.location.origin) return;

            this.ensureResourceHint('dns-prefetch', CUSTOMIZER_ORIGIN);
            this.ensureResourceHint('preconnect', CUSTOMIZER_ORIGIN);

            let prefetch = document.querySelector('link[data-omni-customizer-prefetch]');
            if (!prefetch) {
                prefetch = document.createElement('link');
                prefetch.rel = 'prefetch';
                prefetch.as = 'document';
                prefetch.dataset.omniCustomizerPrefetch = 'true';
                prefetch.href = productUrl.href;
                document.head.appendChild(prefetch);
                return;
            }

            if (prefetch.href !== productUrl.href) {
                prefetch.href = productUrl.href;
            }
        }

        getCustomizeButtonConfigForProduct(product) {
            const isCustomizable = product.customizable === true || product.customizable === 'true';
            const customizationType = String(product.customizationType || '').trim().toUpperCase();

            return this.getCustomizeButtonConfig(isCustomizable, customizationType);
        }

        getCustomizeButtonConfig(isCustomizable, customizationType) {
            if (!isCustomizable) return null;

            if (customizationType === 'SWE') {
                return {
                    customizationType,
                    className: 'customizable-button',
                    label: 'Customizable',
                    customizeOnly: false
                };
            }

            if (customizationType === 'SPO' || customizationType === 'SUB') {
                return {
                    customizationType,
                    className: 'customize-now-button',
                    label: 'Customize Now',
                    customizeOnly: true
                };
            }

            return null;
        }

        renderModalOptions(product, selectedVariant) {
            const colorOption = product.options?.find(option => option.name.toLowerCase().includes('color'));
            if (!this.variantSelector || !colorOption) return;

            this.selectedModalColor = selectedVariant.options[colorOption.position - 1];
            this.activeModalProduct = product;
            this.activeModalColorOption = colorOption;
            const hasSwatches = colorOption.values.some(value => value.swatchHtml);
            const showSizeGrid = !this.activePackageProduct && !this.activeContactOnly && this.activeCustomizeButtonConfig?.customizeOnly !== true;
            const showStandaloneSizeGuide = !this.activePackageProduct && !showSizeGrid && this.hasSizeChartKey(product);

            this.variantSelector.innerHTML = `
        <div class="curated-looks-variant-label-${this.sectionId}" id="curated-looks-color-label-${this.sectionId}">Color</div>
        <div
          class="curated-looks-variant-options-${this.sectionId}"
          data-swatch-mode="${hasSwatches ? 'true' : 'false'}"
          role="radiogroup"
          aria-labelledby="curated-looks-color-label-${this.sectionId}"
        >
          ${colorOption.values.map(value => `
            <button
              type="button"
              class="curated-looks-variant-option-${this.sectionId} ${this.selectedModalColor === value.name ? 'selected' : ''}"
              data-color-value="${this.escapeAttribute(value.name)}"
              role="radio"
              aria-checked="${this.selectedModalColor === value.name ? 'true' : 'false'}"
              aria-label="Select color ${this.escapeAttribute(value.name)}"
            >
              ${hasSwatches ? `
                <span class="curated-looks-swatch-wrap-${this.sectionId}">
                  ${value.swatchHtml || ''}
                </span>
              ` : ''}
              <span>${this.escapeHtml(value.name)}</span>
            </button>
          `).join('')}
        </div>

        ${showSizeGrid ? `
          <div data-size-grid-wrapper>
            ${this.renderSizeGrid(product, colorOption, this.selectedModalColor)}
          </div>
        ` : ''}
        ${showStandaloneSizeGuide ? `
          <div
            class="curated-looks-size-guide-only-${this.sectionId}"
            style="display: flex; justify-content: flex-end; width: 100%; margin: 8px 0 12px;"
          >
            <a
              href="${this.escapeAttribute(this.getSizeGuideUrl(product))}"
              class="curated-looks-size-guide-link-${this.sectionId}"
              style="font-size: 12px; font-weight: 400; text-decoration: underline; color: var(--color-omni-midnight, #22325f); white-space: nowrap;"
            >
              Size Guide
            </a>
          </div>
        ` : ''}
      `;
            this.activeModalSingleSize = this.isSingleSizeProduct(product);
            this.updatePurchaseActionState();
        }

        selectModalColor(selectedColor) {
            if (!selectedColor || !this.activeModalProduct || !this.activeModalColorOption) return;
            if (this.normalizeValue(selectedColor) === this.normalizeValue(this.selectedModalColor)) return;

            const product = this.activeModalProduct;
            const colorOption = this.activeModalColorOption;
            this.selectedModalColor = selectedColor;

            const colorVariants = product.variants.filter(variant => {
                return variant.options[colorOption.position - 1] === selectedColor;
            });
            const selectedColorVariant = colorVariants.find(variant => variant.available) || colorVariants[0];

            if (selectedColorVariant) {
                this.activeVariant = selectedColorVariant;
                this.activeModalColor = selectedColor;

                // Price changes with the variant, but reviews belong to the
                // product. Do not remount PowerReviews for every swatch click.
                this.renderModalPrice(selectedColorVariant, product, { skipReviews: true });
                this.renderModalGallery(product, selectedColorVariant);
                this.syncModalProductLinkVariant(product.url || '', selectedColorVariant.id);
            }

            this.variantSelector?.querySelectorAll('[data-color-value]').forEach(button => {
                const isSelected = button.dataset.colorValue === selectedColor;
                button.classList.toggle('selected', isSelected);
                button.setAttribute('aria-checked', isSelected ? 'true' : 'false');
            });

            const sizeGridWrapper = this.variantSelector?.querySelector('[data-size-grid-wrapper]');
            if (sizeGridWrapper) {
                sizeGridWrapper.innerHTML = this.renderSizeGrid(product, colorOption, selectedColor);
            }

            // The customizer destination was already warmed when the modal
            // opened. Re-prefetching a full PDP for each variant is wasteful.
            this.renderCustomizeAction(product, { warmCustomizer: false });
            this.schedulePurchaseActionStateUpdate();
        }

        renderModalGallery(product, variant) {
            this.galleryImages = this.getVariantImages(product, variant);
            this.galleryIndex = 0;

            if (this.gallery) {
                this.gallery.classList.toggle('has-multiple', this.galleryImages.length > 1);
            }

            if (this.galleryPrev) this.galleryPrev.hidden = this.galleryImages.length <= 1;
            if (this.galleryNext) this.galleryNext.hidden = this.galleryImages.length <= 1;

            this.renderGalleryDots();
            this.showGalleryImage(0);
            this.attachGallerySwipe();
        }

        getVariantImages(product, variant) {
            const mediaItems = Array.isArray(variant.imagery) ? variant.imagery : [];
            const normalizedMedia = mediaItems
                .map(item => this.normalizeGalleryItem(item))
                .filter(item => item?.src);

            if (normalizedMedia.length) {
                return this.dedupeGalleryMedia(normalizedMedia);
            }

            if (variant.image) {
                normalizedMedia.push({ type: 'image', src: variant.image });
            }

            if (product.image) {
                normalizedMedia.push({ type: 'image', src: product.image });
            }

            return this.dedupeGalleryMedia(normalizedMedia);
        }

        normalizeGalleryItem(item) {
            if (!item) return null;

            if (typeof item === 'string') {
                return { type: 'image', src: item };
            }

            return {
                type: item.type === 'video' ? 'video' : 'image',
                src: item.src || '',
                poster: item.poster || ''
            };
        }

        dedupeGalleryMedia(mediaItems) {
            const seen = new Set();

            return mediaItems.filter(item => {
                const key = `${item.type}:${item.src}`;
                if (seen.has(key)) return false;
                seen.add(key);
                return true;
            });
        }

        renderGalleryDots() {
            if (!this.galleryDots) return;

            if (this.galleryImages.length <= 1) {
                this.galleryDots.innerHTML = '';
                return;
            }

            this.galleryDots.innerHTML = this.galleryImages.map((image, index) => `
        <button
          type="button"
          class="curated-looks-modal-gallery-dot-${this.sectionId} ${index === this.galleryIndex ? 'active' : ''}"
          data-gallery-index="${index}"
          aria-label="View image ${index + 1}"
          aria-current="${index === this.galleryIndex ? 'true' : 'false'}"
        ></button>
      `).join('');
        }

        showGalleryImage(index) {
            if (!this.galleryImages.length) return;

            const boundedIndex = (index + this.galleryImages.length) % this.galleryImages.length;
            const media = this.galleryImages[boundedIndex];
            this.galleryIndex = boundedIndex;

            if (media.type === 'video') {
                this.showVideoMedia(media);
            } else {
                this.showImageMedia(media);
            }

            this.galleryDots?.querySelectorAll('[data-gallery-index]').forEach(dot => {
                const isActive = Number(dot.dataset.galleryIndex) === boundedIndex;
                dot.classList.toggle('active', isActive);
                dot.setAttribute('aria-current', isActive ? 'true' : 'false');
            });

            this.preloadGalleryMedia(boundedIndex + 1);
        }

        showImageMedia(media) {
            if (!this.modalImg) return;

            if (this.modalVideo) {
                const hadVideoResource = this.modalVideo.hasAttribute('src') ||
                    this.modalVideo.hasAttribute('poster');

                if (!this.modalVideo.paused) this.modalVideo.pause();
                this.modalVideo.hidden = true;
                this.modalVideo.removeAttribute('src');
                this.modalVideo.removeAttribute('poster');
                if (hadVideoResource) this.modalVideo.load();
            }

            this.setVideoToggleVisible(false);

            this.modalImg.hidden = false;
            this.modalImg.decoding = 'async';
            this.modalImg.loading = 'eager';
            this.modalImg.alt = this.activeProduct?.title || '';

            if (this.modalImg.src !== media.src) {
                this.modalImg.src = media.src;
            }
        }

        showVideoMedia(media) {
            if (!this.modalVideo) return;

            if (this.modalImg) {
                this.modalImg.hidden = true;
            }

            this.modalVideo.hidden = false;
            this.modalVideo.muted = true;
            this.modalVideo.loop = true;
            this.modalVideo.playsInline = true;
            this.setVideoToggleVisible(true);

            if (media.poster) {
                this.modalVideo.poster = media.poster;
            } else {
                this.modalVideo.removeAttribute('poster');
            }

            if (this.modalVideo.currentSrc !== media.src && this.modalVideo.src !== media.src) {
                this.modalVideo.src = media.src;
                this.modalVideo.load();
            }

            this.modalVideo.onloadedmetadata = () => this.scheduleVideoTogglePlacement();

            this.modalVideo.play?.()
                .then(() => this.updateVideoToggleState())
                .catch(() => this.updateVideoToggleState());
            this.updateVideoToggleState();
            this.scheduleVideoTogglePlacement();
        }

        setVideoToggleVisible(isVisible) {
            if (!this.videoToggle) return;

            this.videoToggle.hidden = !isVisible;

            if (isVisible) {
                this.updateVideoToggleState();
                this.scheduleVideoTogglePlacement();
            }
        }

        updateVideoToggleState() {
            if (!this.videoToggle || !this.modalVideo) return;

            const isPaused = this.modalVideo.paused;
            this.videoToggle.dataset.videoState = isPaused ? 'paused' : 'playing';
            this.videoToggle.setAttribute('aria-label', isPaused ? 'Play video' : 'Pause video');
        }

        scheduleVideoTogglePlacement() {
            if (this.videoTogglePlacementFrame) {
                cancelAnimationFrame(this.videoTogglePlacementFrame);
            }

            this.videoTogglePlacementFrame = requestAnimationFrame(() => {
                this.videoTogglePlacementFrame = requestAnimationFrame(() => {
                    this.videoTogglePlacementFrame = null;
                    this.updateVideoTogglePlacement();
                });
            });
        }

        updateVideoTogglePlacement() {
            if (!this.videoToggle || !this.modalVideo || this.modalVideo.hidden || !this.galleryTrack) return;
            if (!this.modal?.classList.contains('active')) return;

            const trackRect = this.galleryTrack.getBoundingClientRect();
            const naturalWidth = this.modalVideo.videoWidth || trackRect.width;
            const naturalHeight = this.modalVideo.videoHeight || trackRect.height;

            if (!trackRect.width || !trackRect.height || !naturalWidth || !naturalHeight) return;

            const renderedScale = Math.min(trackRect.width / naturalWidth, trackRect.height / naturalHeight);
            const renderedWidth = naturalWidth * renderedScale;
            const renderedHeight = naturalHeight * renderedScale;
            const insetX = Math.max(0, (trackRect.width - renderedWidth) / 2);
            const insetY = Math.max(0, (trackRect.height - renderedHeight) / 2);
            const offset = 12;

            this.videoToggle.style.left = `${insetX + offset}px`;
            this.videoToggle.style.bottom = `${insetY + offset}px`;
        }

        toggleModalVideo() {
            if (!this.modalVideo || this.modalVideo.hidden) return;

            if (this.modalVideo.paused) {
                this.modalVideo.play?.()
                    .then(() => this.updateVideoToggleState())
                    .catch(() => this.updateVideoToggleState());
                return;
            }

            this.modalVideo.pause();
            this.updateVideoToggleState();
        }

        preloadGalleryMedia(index) {
            if (this.galleryImages.length <= 1) return;
            if (this.isMobile()) return;

            const media = this.galleryImages[index % this.galleryImages.length];
            if (!media?.src) return;

            if (media.type === 'image') {
                const image = new Image();
                image.src = media.src;
                return;
            }

            if (media.type === 'video') {
                const video = document.createElement('video');
                video.preload = 'metadata';
                video.muted = true;
                video.src = media.src;
            }
        }

        isContactOnlyProduct(product) {
            const value =
                product?.hidePurchaseControls ??
                product?.hide_purchase_controls ??
                product?.metafields?.product?.hide_purchase_controls ??
                product?.metafield?.product?.hide_purchase_controls;

            if (value === true || value === 1) return true;

            if (typeof value === 'string') {
                return ['true', '1', 'yes'].includes(value.trim().toLowerCase());
            }

            if (value && typeof value === 'object' && 'value' in value) {
                return this.isContactOnlyProduct({ hidePurchaseControls: value.value });
            }

            return false;
        }

        isPackageProduct(product) {
            const productType = product?.productType ?? product?.product_type ?? product?.type ?? '';
            return this.normalizeValue(productType) === 'package';
        }

        isShoeProduct(product) {
            const productType = product?.productType ?? product?.product_type ?? product?.type ?? '';
            const title = product?.title ?? '';
            const handle = product?.handle ?? '';
            const url = product?.url ?? '';
            const tags = Array.isArray(product?.tags) ? product.tags.join(' ') : product?.tags ?? '';
            const itemNumber = this.getProductItemNumber(product, product?.variants?.[0]) || '';
            const shoeSignals = [productType, title, handle, url, tags].map(value => String(value)).join(' ');
            const hasShoeStyleCode = /^S\d{3,}[A-Z]?$/i.test(String(itemNumber).trim());
            const hasShoeHandleCode = /(?:^|-)s\d{3,}[a-z]?(?:$|[/?#])/i.test(`${handle} ${url}`);
            return /shoe|footwear/i.test(shoeSignals) || hasShoeStyleCode || hasShoeHandleCode;
        }

        getContactUrl(product) {
            return product.contactUrl || '/pages/contact';
        }

        hasSizeChartKey(product) {
            const value =
                product?.sizeChartKey ??
                product?.size_chart_key ??
                product?.metafields?.product?.size_chart_key ??
                product?.metafield?.product?.size_chart_key;

            if (value && typeof value === 'object' && 'value' in value) {
                return this.hasSizeChartKey({ sizeChartKey: value.value });
            }

            return String(value || '').trim().length > 0;
        }

        getSizeGuideUrl(product) {
            const url = product?.url || '';
            if (!url) return '#sizing';

            return `${url.split('#')[0]}#sizing`;
        }

        getPowerReviewsProductId(product) {
            return product?.id ??
                product?.productId ??
                product?.shopifyProductId ??
                '';
        }

        renderPowerReviewsCardSnippet(product, context = 'card') {
            const productId = this.getPowerReviewsProductId(product);
            if (!productId) return '';

            const escapedProductId = this.escapeAttribute(productId);
            const escapedContext = this.escapeAttribute(context);
            const snippetId = context === 'modal-price'
                ? 'pr-reviewsnippet'
                : `pr-reviewsnippet-${escapedContext}-${escapedProductId}`;

            return `
                <div
                    class="pr-snippet-wrapper"
                    data-pr-product-id="${escapedProductId}"
                    data-pr-context="${escapedContext}"
                    data-pr-product-url="${this.escapeAttribute(this.getProductReviewsUrl(product))}"
                >
                    <div
                        id="${snippetId}"
                        data-powerreviews-card-snippet
                        data-pr-component="ReviewSnippet"
                        data-pr-page_id="${escapedProductId}"
                        lang="en"
                    ></div>
                </div>
            `;
        }

        renderPowerReviewsCardSnippets() {
            this.grid?.querySelectorAll('[data-powerreviews-card-snippet]').forEach(snippet => {
                const productId = snippet.dataset.prPage_id || '';
                const snippetId = snippet.id || '';
                const productUrl = snippet.closest('.pr-snippet-wrapper')?.dataset.prProductUrl || '';

                if (!productId || !snippetId) return;

                snippet.innerHTML = '';
                snippet.removeAttribute('data-pr-mounted');
                this.refreshPowerReviewsSnippets(productId, snippetId, 0, productUrl);
            });
        }

        schedulePowerReviewsCardSnippets() {
            if (this.powerReviewsIdleCallback !== null && 'cancelIdleCallback' in window) {
                window.cancelIdleCallback(this.powerReviewsIdleCallback);
            }
            if (this.powerReviewsIdleTimer !== null) {
                window.clearTimeout(this.powerReviewsIdleTimer);
            }

            const render = () => {
                this.powerReviewsIdleCallback = null;
                this.powerReviewsIdleTimer = null;
                if (this.isConnected) this.renderPowerReviewsCardSnippets();
            };

            if ('requestIdleCallback' in window) {
                this.powerReviewsIdleCallback = window.requestIdleCallback(render, { timeout: 1500 });
                return;
            }

            this.powerReviewsIdleTimer = window.setTimeout(render, 250);
        }

        getProductReviewsUrl(productOrUrl) {
            const url = typeof productOrUrl === 'string' ? productOrUrl : productOrUrl?.url || '';
            if (!url) return '#pr-snippet-wrapper';

            return `${url.split('#')[0]}#pr-snippet-wrapper`;
        }

        updatePowerReviewsReviewCountLinks(snippetId, productUrl) {
            const snippet = document.getElementById(snippetId);
            if (!snippet) return;

            snippet.querySelectorAll('.pr-snippet-write-review-link').forEach(link => {
                link.remove();
            });

            const resolvedProductUrl =
                productUrl ||
                snippet.closest('[data-pr-product-url]')?.dataset.prProductUrl ||
                '';

            if (!resolvedProductUrl) return;

            snippet.querySelectorAll('.pr-snippet-review-count').forEach(link => {
                const text = String(link.textContent || '').trim().toLowerCase();
                if (!text || text.includes('no reviews')) return;

                link.setAttribute('href', resolvedProductUrl);
            });
        }

        getPowerReviewsQueuedRenderConfig() {
            const queue = window.pwr?.q;
            if (!Array.isArray(queue)) return null;

            const renderCall = queue.find(call => {
                const args = Array.from(call || []);
                return args[0] === 'render' && args[1] && typeof args[1] === 'object';
            });

            return renderCall ? Array.from(renderCall)[1] : null;
        }

        getPowerReviewsScriptConfig() {
            if (window.__omniPowerReviewsScriptConfig) {
                return window.__omniPowerReviewsScriptConfig;
            }

            const config = {};
            const scriptText = Array.from(document.scripts)
                .map(script => script.textContent || '')
                .filter(text => text.includes('POWERREVIEWS') || text.includes('pwr(') || text.includes('merchant_id'))
                .join('\n');

            const fields = [
                'api_key',
                'locale',
                'merchant_group_id',
                'merchant_id',
                'review_wrapper_url'
            ];

            fields.forEach(field => {
                const match = scriptText.match(new RegExp(`${field}["']?\\s*:\\s*["']([^"']+)["']`));
                if (match?.[1]) config[field] = match[1];
            });

            if (Object.keys(config).length) {
                window.__omniPowerReviewsScriptConfig = config;
            }

            return config;
        }

        getPowerReviewsSettingsConfig() {
            const dataset = this.powerReviewsWrapper?.dataset || {};
            const config = {};

            if (dataset.prMerchantGroupId) config.merchant_group_id = dataset.prMerchantGroupId;
            if (dataset.prMerchantId) config.merchant_id = dataset.prMerchantId;
            if (dataset.prLocale) config.locale = dataset.prLocale;
            if (dataset.prApiKey) config.api_key = dataset.prApiKey;

            return config;
        }

        getPowerReviewsRenderConfig(productId, snippetId) {
            const baseConfig = {
                locale: 'en_US',
                ...this.getPowerReviewsScriptConfig(),
                ...this.getPowerReviewsSettingsConfig(),
                ...(this.getPowerReviewsQueuedRenderConfig() || {})
            };
            const baseComponents = baseConfig.components || {};

            return {
                ...baseConfig,
                page_id: String(productId),
                components: {
                    ...baseComponents,
                    ReviewSnippet: snippetId
                }
            };
        }

        refreshPowerReviewsSnippets(productId = '', snippetId = 'pr-reviewsnippet', attempt = 0, productUrl = '') {
            window.__loadOmniPowerReviews?.();

            const render = () => {
                try {
                    if (productId && snippetId) {
                        const config = this.getPowerReviewsRenderConfig(productId, snippetId);
                        document.dispatchEvent(new CustomEvent('omni-quickview:powerreviews-render', {
                            detail: { productId, snippetId, config }
                        }));

                        if (typeof window.pwr === 'function') {
                            window.pwr('render', config);
                            return;
                        }

                        window.POWERREVIEWS?.display?.render?.(config);
                        return;
                    }

                    if (typeof window.pwr === 'function') {
                        window.pwr('render');
                        return;
                    }

                    window.POWERREVIEWS?.display?.render?.();
                } catch (error) {
                    document.dispatchEvent(new CustomEvent('omni-quickview:powerreviews-ready'));
                }
            };

            requestAnimationFrame(render);
            [250, 1250].forEach(delay => {
                window.setTimeout(() => this.updatePowerReviewsReviewCountLinks(snippetId, productUrl), delay);
            });

            if (attempt < 1) {
                window.setTimeout(() => {
                    const snippet = document.getElementById(snippetId);
                    this.updatePowerReviewsReviewCountLinks(snippetId, productUrl);
                    if (snippet && !snippet.dataset.prMounted && !snippet.querySelector('.p-w-r')) {
                        this.refreshPowerReviewsSnippets(productId, snippetId, attempt + 1, productUrl);
                    }
                }, 350 * (attempt + 1));
            }
        }

        renderModalPowerReviews(product) {
            if (!this.powerReviewsWrapper || !this.powerReviewsSnippet) return;

            const productId = this.getPowerReviewsProductId(product);

            if (!productId) {
                this.activePowerReviewsProductId = '';
                this.powerReviewsWrapper.hidden = true;
                this.powerReviewsWrapper.dataset.prProductId = '';
                if (this.powerReviewsSnippet) {
                    this.powerReviewsSnippet.dataset.prPage_id = '';
                    this.powerReviewsSnippet.innerHTML = '';
                    this.powerReviewsSnippet.removeAttribute('data-pr-mounted');
                }
                return;
            }

            const productIdString = String(productId);
            const snippetId = `pr-reviewsnippet-${this.sectionId}-${productIdString}`;

            if (
                this.activePowerReviewsProductId === productIdString &&
                this.powerReviewsWrapper.dataset.prProductId === productIdString &&
                this.powerReviewsSnippet?.id === snippetId &&
                (this.powerReviewsSnippet.dataset.prMounted || this.powerReviewsSnippet.querySelector('.p-w-r'))
            ) {
                this.powerReviewsWrapper.hidden = false;
                this.powerReviewsWrapper.dataset.prProductUrl = this.getProductReviewsUrl(product);
                return;
            }

            const freshSnippet = document.createElement('div');

            freshSnippet.id = snippetId;
            freshSnippet.dataset.powerreviewsSnippet = '';
            freshSnippet.dataset.prComponent = 'ReviewSnippet';
            freshSnippet.dataset.prPage_id = productIdString;
            freshSnippet.lang = 'en';

            this.powerReviewsSnippet.replaceWith(freshSnippet);
            this.powerReviewsSnippet = freshSnippet;

            this.powerReviewsWrapper.hidden = false;
            this.powerReviewsWrapper.dataset.prProductId = productIdString;
            this.powerReviewsWrapper.dataset.prProductUrl = this.getProductReviewsUrl(product);
            this.activePowerReviewsProductId = productIdString;
            this.refreshPowerReviewsSnippets(productIdString, snippetId, 0, this.powerReviewsWrapper.dataset.prProductUrl);
        }

        renderPendingModalPowerReviews() {
            if (!this.pendingPowerReviewsProduct) return;

            const product = this.pendingPowerReviewsProduct;
            this.pendingPowerReviewsProduct = null;

            window.setTimeout(() => this.renderModalPowerReviews(product), 0);
        }

        renderModalPrice(variant, product = this.activeProduct, options = {}) {
            if (!this.modalPrice || !variant) return;

            const price = Number(variant.price || 0);
            const compareAtPrice = Number(
                variant.compareAtPrice ??
                variant.compare_at_price ??
                variant.compare_at_price_min ??
                0
            );
            const hasCompareAtPrice = compareAtPrice > price;
            const itemNumber = this.getProductItemNumber(product, variant);

            this.modalPrice.innerHTML = `
        <span style="display: flex; flex-direction: column; gap: 4px;">
          ${itemNumber ? `
            <span style="font-size: 14px; font-weight: 500; color: var(--color-omni-midnight, #22325f);">
              Item No. ${this.escapeHtml(itemNumber)}
            </span>
          ` : ''}
          <span style="display: inline-flex; align-items: center; gap: 8px; flex-wrap: wrap;">
            <span>${this.escapeHtml(this.formatMoney(price))}</span>
            ${hasCompareAtPrice ? `
              <s style="color: #777; font-weight: 400;">
                ${this.escapeHtml(this.formatMoney(compareAtPrice))}
              </s>
            ` : ''}
          </span>
        </span>
      `;
            if (options.skipReviews) {
                return;
            }

            if (options.deferReviews) {
                this.pendingPowerReviewsProduct = product;
            } else {
                this.renderModalPowerReviews(product);
            }
        }

        getProductItemNumber(product, variant) {
            return (
                product?.itemNumber ||
                product?.item_number ||
                product?.Item_number ||
                variant?.itemNumber ||
                variant?.item_number ||
                ''
            );
        }

        goToGalleryImage(index) {
            if (this.galleryImages.length <= 1) return;
            this.showGalleryImage(index);
        }

        attachGallerySwipe() {
            if (!this.galleryTrack || this.galleryTrack.dataset.swipeReady === 'true') return;

            this.galleryTrack.dataset.swipeReady = 'true';

            this.galleryTrack.addEventListener('pointerdown', event => {
                this.galleryPointerStartX = event.clientX;
            });

            this.galleryTrack.addEventListener('pointerup', event => {
                if (this.galleryPointerStartX === null || this.galleryImages.length <= 1) return;

                const deltaX = event.clientX - this.galleryPointerStartX;
                this.galleryPointerStartX = null;

                if (Math.abs(deltaX) < 40) return;
                this.goToGalleryImage(this.galleryIndex + (deltaX < 0 ? 1 : -1));
            });

            this.galleryTrack.addEventListener('pointercancel', () => {
                this.galleryPointerStartX = null;
            });
        }

        renderModalDescription(description) {
            if (!this.modalDescription) return;

            Object.assign(this.modalDescription.style, {
                display: '-webkit-box',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: '4',
                maxHeight: '6.4em',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
            });

            this.modalDescription.innerHTML = this.sanitizeProductHtml(description);
        }

        sanitizeProductHtml(html) {
            const template = document.createElement('template');
            template.innerHTML = String(html || '');

            template.content.querySelectorAll('script, style, iframe, object, embed').forEach(node => {
                node.remove();
            });

            template.content.querySelectorAll('*').forEach(node => {
                Array.from(node.attributes).forEach(attribute => {
                    const name = attribute.name.toLowerCase();

                    if (name.startsWith('on')) {
                        node.removeAttribute(attribute.name);
                    }

                    if ((name === 'href' || name === 'src') && /^javascript:/i.test(attribute.value || '')) {
                        node.removeAttribute(attribute.name);
                    }
                });
            });

            return template.innerHTML;
        }

        renderSizeGrid(product, colorOption, selectedColor) {
            if (this.isPackageProduct(product) || this.isContactOnlyProduct(product)) return '';

            const sizeVariants = product.variants.filter(variant => {
                return variant.options[colorOption.position - 1] === selectedColor;
            });
            const isSingleSize = this.isSingleSizeProduct(product);
            const isShoe = this.isShoeProduct(product);
            const showSizeGuide = this.hasSizeChartKey(product);


            const renderRows = variants => variants.map(variant => {
                const inputId = `curated-looks-qty-${this.sectionId}-${variant.id}`;
                const sizeLabel = variant.size || 'quantity';
                return `
            <div class="curated-looks-size-row-${this.sectionId} ${!variant.available ? `curated-looks-size-row-sold-out-${this.sectionId}` : ''}" data-variant-id="${this.escapeAttribute(variant.id)}">
              <input
                id="${this.escapeAttribute(inputId)}"
                type="number"
                min="0"
                step="1"
                value=""
                class="curated-looks-size-qty-${this.sectionId}"
                data-variant-id="${this.escapeAttribute(variant.id)}"
                aria-label="Quantity for size ${this.escapeAttribute(sizeLabel)}"
                inputmode="numeric"
                ${!variant.available ? 'disabled' : ''}
              >
              <label class="curated-looks-size-label-${this.sectionId}" for="${this.escapeAttribute(inputId)}">
                <span>${this.escapeHtml(variant.size || 'Qty')}</span>
                ${!variant.available ? `<small class="curated-looks-sold-out-${this.sectionId}">(Sold out)</small>` : ''}
              </label>
            </div>
          `;
            }).join('');

            const renderSizeGroup = (label, variants, extraClass = '') => {
                if (!variants.length) return '';
                return `
            <div class="curated-looks-size-accordion-${this.sectionId} ${extraClass}">
              ${label ? `<div class="curated-looks-size-accordion-summary-${this.sectionId}">${this.escapeHtml(label)}</div>` : ''}
              <div class="curated-looks-size-accordion-grid-${this.sectionId}">
                ${renderRows(variants)}
              </div>
            </div>
          `;
            };

            let sizeRowsHtml = renderRows(sizeVariants);
            if (!isSingleSize && isShoe) {
                const childSizes = sizeVariants.filter(variant => /y/i.test(variant.size || ''));
                const adultSizes = sizeVariants.filter(variant => !/y/i.test(variant.size || ''));
                sizeRowsHtml = [
                    renderSizeGroup('Child Sizes', childSizes),
                    renderSizeGroup('Adult Sizes', adultSizes)
                ].join('');
            } else if (!isSingleSize) {
                sizeRowsHtml = renderSizeGroup(
                    '',
                    sizeVariants,
                    `curated-looks-general-size-accordion-${this.sectionId}`
                );
            }

            return `
        <div class="curated-looks-size-grid-${this.sectionId}">
          <div
            class="curated-looks-size-grid-header-${this.sectionId}"
            style="grid-column: 1 / -1; display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; width: 100%; margin-bottom: 4px;"
          >
            <div class="curated-looks-variant-label-${this.sectionId}" style="color: var(--color-omni-midnight, #22325f);">
              ${isSingleSize ? 'Quantity' : 'Quantity by Size'}
            </div>
            ${showSizeGuide ? `
              <a
                href="${this.escapeAttribute(this.getSizeGuideUrl(product))}"
                class="curated-looks-size-guide-link-${this.sectionId}"
                style="font-size: 12px; font-weight: 400; text-decoration: underline; color: var(--color-omni-midnight, #22325f); white-space: nowrap;"
              >
                Size Guide
              </a>
            ` : ''}
          </div>

          ${sizeRowsHtml}
        </div>
      `;
        }

        isSingleSizeProduct(product) {
            const uniqueSizes = [...new Set((product?.variants || []).map(variant => variant.size))].filter(Boolean);
            return uniqueSizes.length === 1 || (uniqueSizes.length === 0 && product?.variants?.length === 1);
        }

        isActiveQuickViewSelectionSoldOut(product) {
            const variants = Array.isArray(product?.variants) ? product.variants : [];
            if (!variants.length) return false;

            const colorPosition = Number(this.activeModalColorOption?.position || 0);
            const selectedColor = this.selectedModalColor;
            const visibleVariants = colorPosition > 0 && selectedColor
                ? variants.filter(variant => variant.options?.[colorPosition - 1] === selectedColor)
                : variants;

            return visibleVariants.length > 0 && !visibleVariants.some(variant => variant.available === true);
        }

        getSelectedQuantityTotal() {
            if (!this.modal) return 0;

            return Array.from(this.modal.querySelectorAll(`.curated-looks-size-qty-${this.sectionId}`))
                .reduce((total, input) => {
                    const quantity = parseInt(input.value || '0', 10);
                    return total + (Number.isFinite(quantity) && quantity > 0 ? quantity : 0);
                }, 0);
        }

        setPurchaseActionsHidden(hidden) {
            (this.addToCartButtons || []).forEach(button => {
                button.hidden = hidden;
            });
            if (this.mobileCartAction) this.mobileCartAction.hidden = hidden;
            requestAnimationFrame(() => this.updateMobilePurchaseActionPlacement());
        }

        hasAvailableQuantityInput() {
            return Boolean(
                this.modal?.querySelector(`.curated-looks-size-qty-${this.sectionId}:not(:disabled)`)
            );
        }

        schedulePurchaseActionStateUpdate() {
            if (this.purchaseActionFrame !== null) return;

            this.purchaseActionFrame = requestAnimationFrame(() => {
                this.purchaseActionFrame = null;
                if (this.isConnected) this.updatePurchaseActionState();
            });
        }

        updatePurchaseActionState(options = {}) {
            const quantityTotal = this.getSelectedQuantityTotal();
            const prompt = this.activeModalSingleSize ? 'Add to Cart' : 'Add to Cart';
            const isLoading = options.loading === true;
            const isSoldOut = !this.hasAvailableQuantityInput();
            const label = isSoldOut
                ? 'Sold Out — Continue Browsing'
                : quantityTotal > 0
                    ? 'Add to Cart'
                    : prompt;

            (this.addToCartButtons || []).forEach(button => {
                const soldOutValue = isSoldOut ? 'true' : 'false';
                if (button.textContent !== label) button.textContent = label;
                if (button.disabled !== isLoading) button.disabled = isLoading;
                if (button.dataset.soldOutContinue !== soldOutValue) {
                    button.dataset.soldOutContinue = soldOutValue;
                }
                if (button.getAttribute('aria-label') !== label) {
                    button.setAttribute('aria-label', label);
                }
            });
        }

        updateMobilePurchaseActionPlacement() {
            if (!this.mobileCartAction || !this.productLink || !this.modalBody) return;

            const shouldFloat = this.isMobile() &&
                this.modal?.classList.contains('active') &&
                !this.mobileCartAction.hidden;

            if (!shouldFloat) {
                this.mobileCartAction.classList.remove('is-floating');
                return;
            }

            const visualViewport = window.visualViewport;
            const keyboardOffset = visualViewport
                ? Math.max(0, Math.round(window.innerHeight - visualViewport.height - visualViewport.offsetTop))
                : 0;
            this.mobileCartAction.style.setProperty('--omni-qv-mobile-bottom', `${12 + keyboardOffset}px`);

            const linkRect = this.productLink.getBoundingClientRect();
            const modalBodyRect = this.modalBody.getBoundingClientRect();
            const productLinkIsVisible = linkRect.bottom > modalBodyRect.top &&
                linkRect.top < modalBodyRect.bottom;

            this.mobileCartAction.classList.toggle('is-floating', !productLinkIsVisible);
        }

        focusQuantitySelector() {
            const quantityArea = this.variantSelector?.querySelector('[data-size-grid-wrapper]');
            const firstQuantityInput = quantityArea?.querySelector(`.curated-looks-size-qty-${this.sectionId}:not(:disabled)`);
            if (!quantityArea) return;

            quantityArea.querySelectorAll('details').forEach(details => details.open = true);
            quantityArea.scrollIntoView({ behavior: 'smooth', block: 'center' });
            firstQuantityInput?.focus({ preventScroll: true });
        }

        openMiniCart() {
            requestAnimationFrame(() => {
                const cartDrawer = document.querySelector('cart-drawer-component');
                if (!cartDrawer?.isConnected) return;

                const dialog = cartDrawer.querySelector('dialog');
                if (dialog?.open) return;

                if (typeof cartDrawer.open === 'function') {
                    cartDrawer.open();
                } else if (typeof cartDrawer.showDialog === 'function') {
                    cartDrawer.showDialog();
                }
            });
        }

        async addSelectedSizesToCart() {
            if (!this.modal || !this.addToCartBtn || !this.loading || !this.errorDiv) return;

            const sizeInputs = this.modal.querySelectorAll(`.curated-looks-size-qty-${this.sectionId}`);
            const items = Array.from(sizeInputs)
                .map(input => ({
                    id: input.dataset.variantId,
                    quantity: parseInt(input.value || '0', 10)
                }))
                .filter(item => item.id && item.quantity > 0);

            if (!items.length) {
                this.errorDiv.textContent = 'Please enter a quantity for at least one size.';
                this.errorDiv.classList.add('active');
                this.errorDiv.focus?.({ preventScroll: true });
                return;
            }

            this.updatePurchaseActionState({ loading: true });
            this.loading.classList.add('active');
            this.errorDiv.classList.remove('active');
            this.errorDiv.textContent = '';

            try {
                // Use Horizon's native cart event/section pipeline. Replacing the
                // entire live cart drawer here can race its connected callbacks
                // and dialog state, especially on Searchspring result pages.
                const { CartAddEvent } = await loadThemeEvents();
                const cartSectionIds = Array.from(document.querySelectorAll('cart-items-component'))
                    .map(component => component instanceof HTMLElement ? component.dataset.sectionId : '')
                    .filter(Boolean);
                const payload = { items };

                if (cartSectionIds.length) {
                    payload.sections = [...new Set(cartSectionIds)].join(',');
                    payload.sections_url = window.location.pathname;
                }

                const response = await fetch('/cart/add.js', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Accept: 'application/json'
                    },
                    body: JSON.stringify(payload)
                });
                const result = await response.json();

                if (!response.ok || result.status) {
                    throw new Error(result.description || result.message || 'Failed to add to cart');
                }

                this.closeModal();
                this.dispatchEvent(new CartAddEvent({}, String(items[0]?.id || this.sectionId), {
                    source: 'omni-quickview',
                    itemCount: items.reduce((total, item) => total + item.quantity, 0),
                    productId: this.activeProduct?.id,
                    sections: result.sections || {}
                }));
                this.openMiniCart();

                document.dispatchEvent(new CustomEvent('cart:item-added', {
                    detail: { items }
                }));
            } catch (error) {
                this.errorDiv.textContent = 'Unable to add item to cart. Please try again.';
                this.errorDiv.classList.add('active');
                this.errorDiv.focus?.({ preventScroll: true });
            } finally {
                this.updatePurchaseActionState();
                this.loading.classList.remove('active');
            }
        }

        getItemsPerPage() {
            return this.isMobile() ? 2 : 5;
        }

        getTotalPages() {
            const itemsPerPage = this.getItemsPerPage();

            if (this.isMobile()) {
                return Math.max(1, this.allRenderedProducts.length - itemsPerPage + 1);
            }

            return Math.ceil(this.allRenderedProducts.length / itemsPerPage) || 1;
        }

        renderCurrentPage() {
            if (!this.grid) return;

            const isMobile = this.isMobile();
            const itemsPerPage = this.getItemsPerPage();
            const totalPages = this.getTotalPages();

            if (this.currentPage > totalPages) this.currentPage = totalPages;

            let visibleProducts;

            if (isMobile) {
                visibleProducts = [...this.allRenderedProducts];
            } else {
                const start = (this.currentPage - 1) * itemsPerPage;
                const end = start + itemsPerPage;
                visibleProducts = this.allRenderedProducts.slice(start, end);
            }

            if (!isMobile && visibleProducts.length < itemsPerPage) {
                const emptySlots = itemsPerPage - visibleProducts.length;

                for (let i = 0; i < emptySlots; i += 1) {
                    visibleProducts.push(null);
                }
            }

            this.grid.innerHTML = visibleProducts.map(item => {
                if (!item) {
                    return `<div class="curated-looks__card-placeholder-${this.sectionId}" aria-hidden="true"></div>`;
                }

                const { product, variant } = item;
                const imageUrl = variant.image || '';
                const priceFormatted = this.formatMoney(variant.price);
                const powerReviewsSnippet = this.renderPowerReviewsCardSnippet(product, 'card');
                const buttonText = this.data.buttonText || 'View product';

                return `
          <button
            type="button"
            class="curated-looks__card-${this.sectionId}"
            data-curated-looks-product-handle="${this.escapeAttribute(product.handle)}"
            data-curated-looks-variant-id="${this.escapeAttribute(variant.id)}"
            aria-label="Open quick view for ${this.escapeAttribute(product.title)}"
          >
            <div class="curated-looks__image-wrapper-${this.sectionId}">
              ${imageUrl ? `<img src="${this.escapeAttribute(imageUrl)}" alt="${this.escapeAttribute(product.title)}" class="curated-looks__image-${this.sectionId}" loading="lazy">` : ''}
            </div>
            <h3 class="curated-looks__card-title-${this.sectionId}">${this.escapeHtml(product.title)}</h3>
            <div class="curated-looks__price-${this.sectionId}">${priceFormatted}</div>
            ${powerReviewsSnippet ? `<div class="curated-looks__reviews-${this.sectionId}">${powerReviewsSnippet}</div>` : ''}
            <span class="curated-looks__cta-${this.sectionId}">${this.escapeHtml(buttonText)}</span>
          </button>
        `;
            }).join('');

            this.renderPaginationState();
            this.schedulePowerReviewsCardSnippets();
        }

        updateMobilePaginationFromScroll() {
            const card = this.grid?.querySelector(`.curated-looks__card-${this.sectionId}`);
            if (!card || !this.grid) return;

            const gap = 12;
            const step = card.offsetWidth + gap;
            const totalPages = this.getTotalPages();

            this.currentPage = Math.min(
                totalPages,
                Math.max(1, Math.round(this.grid.scrollLeft / step) + 1)
            );

            this.renderPaginationState();
        }

        renderPaginationState() {
            const totalPages = this.getTotalPages();

            if (this.pagination && this.pageStatus) {
                this.pageStatus.textContent = `${this.currentPage} / ${totalPages}`;
                this.pagination.hidden = totalPages <= 1;
            }

            if (this.prevBtn) this.prevBtn.disabled = this.currentPage <= 1;
            if (this.nextBtn) this.nextBtn.disabled = this.currentPage >= totalPages;
        }

        updateColor(color) {
            if (!color) return;

            if (this.normalizeValue(color) !== this.normalizeValue(this.currentColor)) {
                this.currentColor = color;
                this.renderProducts(color);
            }
        }

        setPdpColorCookie(color) {
            if (!color) return;

            document.cookie = `pdp_selected_color=${encodeURIComponent(color)}; path=/; max-age=86400; SameSite=Lax`;
        }

        getPdpColorCookie() {
            const match = document.cookie.match(/(?:^|;\s*)pdp_selected_color=([^;]*)/);
            return match ? decodeURIComponent(match[1]) : '';
        }

        normalizeValue(value) {
            return String(value || '')
                .trim()
                .toLowerCase()
                .replace(/\s+/g, ' ');
        }

        getColorTokens(value) {
            return this.normalizeValue(value)
                .replace(/[^\w\s/-]/g, ' ')
                .split(/[\s/-]+/)
                .filter(token => token && token.length > 1);
        }

        getColorScore(variantColor, selectedColor) {
            const variantTokens = this.getColorTokens(variantColor);
            const selectedTokens = this.getColorTokens(selectedColor);

            if (!variantTokens.length || !selectedTokens.length) return 0;

            if (this.normalizeValue(variantColor) === this.normalizeValue(selectedColor)) {
                return 1000;
            }

            const genericTokens = ['white', 'silver', 'gold', 'black', 'metallic', 'glitter', 'foil'];
            let score = 0;

            selectedTokens.forEach(token => {
                if (variantTokens.includes(token)) {
                    score += genericTokens.includes(token) ? 10 : 100;
                }
            });

            return score;
        }

        getSelectedPdpColor() {
            const cookieColor = this.getPdpColorCookie();
            if (cookieColor) return cookieColor;

            const selectedBulkColor = Array.from(
                document.querySelectorAll('input[type="radio"][name^="color-selector-"]:checked')
            ).find(input => !input.closest('omni-quickview, [data-modal]'));

            if (selectedBulkColor) {
                return selectedBulkColor.dataset.colorName || selectedBulkColor.value || '';
            }

            const selectedLabelColor = Array.from(
                document.querySelectorAll('[id^="selected-color-name-"]')
            ).find(element => !element.closest('omni-quickview, [data-modal]'));

            if (selectedLabelColor) {
                return selectedLabelColor.textContent.trim();
            }

            return '';
        }

        renderProducts(colorValue) {
            if (!this.grid) return;
            if (!this.data?.products || !Array.isArray(this.data.products)) return;

            const productsToRender = this.data.products.map(product => {
                let matchingVariant = null;
                let bestScore = 0;

                product.variants.forEach(variant => {
                    if (!variant.color) return;

                    const score = this.getColorScore(variant.color, colorValue);

                    if (score > bestScore) {
                        bestScore = score;
                        matchingVariant = variant;
                    }
                });

                const fallbackVariant =
                    product.variants.find(variant => variant.available) ||
                    product.variants[0];

                return {
                    product,
                    variant: bestScore > 0 ? matchingVariant : fallbackVariant,
                    hasColorMatch: bestScore > 0
                };
            }).filter(item => item.variant);

            this.hideFallback();
            this.allRenderedProducts = productsToRender;
            this.currentPage = 1;
            this.renderCurrentPage();
        }

        showFallback() {
            this.classList.add('curated-looks--hidden');
            this.fallback?.classList.add('curated-looks--visible');
        }

        hideFallback() {
            this.classList.remove('curated-looks--hidden');
            this.fallback?.classList.remove('curated-looks--visible');
        }

        isMobile() {
            return window.matchMedia('(max-width: 749px)').matches;
        }

        formatMoney(cents) {
            const dollars = (Number(cents || 0) / 100).toFixed(2);
            return `$${dollars}`;
        }

        escapeHtml(value) {
            return String(value ?? '')
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                .replace(/"/g, '&quot;')
                .replace(/'/g, '&#39;');
        }

        escapeAttribute(value) {
            return this.escapeHtml(value);
        }
    }

    customElements.define('omni-quickview', OmniQuickview);

    window.OmniQuickView = {
        ...window.OmniQuickView,
        open(productUrl, variantId) {
            const host = modalRuntime.getGlobalHost();
            if (!host) {
                return Promise.reject(new Error('The global Omni Quick View host is not available.'));
            }

            return host.openProduct(productUrl, variantId);
        }
    };
})();
